import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { DealerApiWrapper, modalStyle } from "../style/DealerApiWrapper";
import { toastError } from "../helpers";

/**
 * Super-admin: issue (or rotate) a dealership's master key on its behalf. The plaintext is in
 * the API response exactly once: it lives only in this modal's state, masked by default, and is
 * dropped when the modal closes. An existing master key is revoked immediately by the server.
 */
const IssueMasterKeyModal = ({ subsidiary, onClose, onIssued }) => {
  const open = subsidiary !== null;
  const [busy, _busy] = useState(false);
  const [plaintext, _plaintext] = useState(null);
  const [showKey, _showKey] = useState(false);

  useEffect(() => {
    // Every open starts clean; the secret never survives a close.
    _plaintext(null);
    _showKey(false);
  }, [subsidiary]);

  const close = () => {
    if (busy) return;
    _plaintext(null);
    onClose();
  };

  const issue = () => {
    _busy(true);
    API.post(`${APP_URLS.DEALER_API}/keys/master/${subsidiary.id}`)
      .then((res) => {
        const resp = res.data;
        if (resp.success === true && resp.data?.plaintext) {
          _plaintext(resp.data.plaintext);
          onIssued();
        } else {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(toastError)
      .finally(() => _busy(false));
  };

  return (
    <Modal open={open} onClose={close}>
      <Box sx={modalStyle}>
        <DealerApiWrapper>
          <h4 className="mb-3">Issue a master key{subsidiary ? ` — ${subsidiary.name}` : ""}</h4>

          {plaintext === null ? (
            <>
              <p>
                The dealership's current master key, if any, is <b>revoked immediately</b>: its DMS stops working until it is
                reconfigured with the new key. Coordinate with the dealer first, and hand the key over through a secure
                channel.
              </p>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="outlined" disabled={busy} onClick={close} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button variant="contained" disabled={busy} onClick={issue} sx={{ textTransform: "none", fontWeight: 600 }}>
                  Issue master key
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-danger fw-bold">Copy it now, it will not be shown again.</p>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="text"
                  readOnly
                  autoComplete="off"
                  spellCheck={false}
                  className="form-control mono"
                  value={showKey ? plaintext : "•".repeat(plaintext.length)}
                />
                <IconButton size="small" onClick={() => _showKey(!showKey)} aria-label={showKey ? "Hide key" : "Show key"}>
                  {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
                <CopyToClipboard text={plaintext} onCopy={() => toast("Text Copied")}>
                  <Button variant="outlined" sx={{ textTransform: "none" }}>
                    Copy
                  </Button>
                </CopyToClipboard>
              </div>
              <div className="d-flex justify-content-end mt-4">
                <Button variant="contained" onClick={close} sx={{ textTransform: "none", fontWeight: 600 }}>
                  I have copied it
                </Button>
              </div>
            </>
          )}
        </DealerApiWrapper>
      </Box>
    </Modal>
  );
};

export default IssueMasterKeyModal;
